<template>
  <UiComponentWrapper
    class="plan"
    :class="{ recommended: isRecommended }"
  >
    <UiComponentWrapper class="recommendation">
      <UiComponentParagraph>{{ props.wording.recommendedForYou }}</UiComponentParagraph>
      <UiComponentParagraph @click="emit('mac:recalculate:click')">
        ({{ props.wording.recalculate }})
      </UiComponentParagraph>
    </UiComponentWrapper>
    <UiComponentWrapper class="outer">
      <UiComponentHeading :level="2">
        {{ props.plan.title }}
      </UiComponentHeading>
      <UiComponentParagraph>{{ props.plan.text }}</UiComponentParagraph>
      <UiComponentWrapper
        class="from"
        :data-information="props.wording.activeContacts"
      >
        <UiComponentHeading
          as="paragraph"
          :level="6"
        >
          {{ props.plan.activeContactsLimit }}
        </UiComponentHeading>
        <UiComponentIcon
          type="info"
          @mouseover="onMouseOver"
          @mouseout="onMouseOut"
        />
      </UiComponentWrapper>
      <AtomButton
        :action="props.plan.cta.action"
        :label="`Column ${props.plan.title} CTA`"
        :to="props.plan.cta.to"
        size="l"
      >
        {{ props.plan.cta.slot }}
      </AtomButton>
      <UiComponentWrapper class="usage">
        <UiComponentHeading :level="3">
          {{ props.wording.usage }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <UiComponentWrapper
            v-for="{ id, icon, text, information } in props.plan.usage"
            :key="`business-plan-usage-${id}`"
            class="feature"
            :class="{ disabled: icon.name === 'x' }"
            :data-information="information"
          >
            <UiComponentIcon :type="icon.name" />
            <UiComponentHeading
              as="paragraph"
              :level="6"
            >
              {{ text }}
            </UiComponentHeading>
            <UiComponentIcon
              v-if="information"
              type="info"
              @mouseover="onMouseOver"
              @mouseout="onMouseOut"
            />
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="add-ons">
        <UiComponentHeading :level="3">
          {{ props.wording.addons }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <UiComponentWrapper
            v-for="{ id, icon, text, information } in props.plan.addons"
            :key="`business-plan-add-on-${id}`"
            class="feature"
            :class="{ disabled: icon.name === 'x' }"
            :data-information="information"
          >
            <UiComponentIcon :type="icon.name" />
            <UiComponentHeading
              as="paragraph"
              :level="6"
            >
              {{ text }}
            </UiComponentHeading>
            <UiComponentIcon
              v-if="information"
              type="info"
              @mouseover="onMouseOver"
              @mouseout="onMouseOut"
            />
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="integrations">
        <UiComponentHeading :level="3">
          {{ props.wording.integrations }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <UiComponentParagraph>{{ props.wording.baseIntegrations }}</UiComponentParagraph>
          <UiComponentWrapper class="details">
            <UiComponentWrapper
              v-for="{ id, name, logo } in props.plan.integrations"
              :key="`business-plan-integration-${id}`"
              class="integration"
              :data-name="name"
            >
              <UiComponentImage
                :alt="logo.alt"
                class="logo"
                :expected-size="20"
                :height="logo.height"
                :source="logo.url"
                :width="logo.width"
              />
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="services">
        <UiComponentHeading :level="3">
          {{ props.wording.services }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <UiComponentWrapper
            v-for="{ id, icon, text, information } in props.plan.services"
            :key="`business-plan-service-${id}`"
            class="feature"
            :class="{ disabled: icon.name === 'x' }"
            :data-information="information"
          >
            <UiComponentIcon :type="icon.name" />
            <UiComponentHeading
              as="paragraph"
              :level="6"
            >
              {{ text }}
            </UiComponentHeading>
            <UiComponentIcon
              v-if="information"
              type="info"
              @mouseover="onMouseOver"
              @mouseout="onMouseOut"
            />
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="separator" />
      <UiComponentWrapper class="used-by">
        <UiComponentHeading :level="3">
          {{ props.wording.usedBy }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <UiComponentImage
            v-for="{ id, logo } in props.plan.usedBy"
            :key="`business-plan-used-by-${id}`"
            :alt="logo.alt"
            class="logo"
            :expected-size="24"
            :extra-params="{
              con: 100,
              gam: 100,
              monochrome: '000000',
              sat: -100,
              trim: 'color',
              vib: 100,
            }"
            :height="logo.height"
            :orientation="logo.width >= logo.height ? 'portrait' : 'landscape'"
            :source="logo.url"
            use-natural-height
            use-natural-width
            :width="logo.width"
          />
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentWrapper>
  </UiComponentWrapper>
</template>

<script setup>
// Emit
const emit = defineEmits([
  'mac:recalculate:click',
])

// Props
const props = defineProps({
  isRecommended: {
    required: false,
    type: Boolean,
  },
  plan: {
    required: true,
    type: Object,
  },
  wording: {
    required: true,
    type: Object,
  },
})

// Methods
function onMouseOver({ currentTarget }) {
  const { parentNode } = currentTarget
  parentNode.style.setProperty('--left', `${currentTarget.getBoundingClientRect().x - parentNode.getBoundingClientRect().x + 8}px`)
  parentNode.classList.add('hoverable')
}

function onMouseOut({ currentTarget }) {
  currentTarget.parentNode.classList.remove('hoverable')
}
</script>

<style lang="postcss" scoped>
.plan {
  @apply relative;

  &.recommended {
    &::before {
      @apply absolute -bottom-px -left-px -right-px -top-7 bg-blizzard-700 content-[''] rounded-3xl;
    }

    .recommendation {
      @apply opacity-100 visible;
    }

    .outer {
      @apply border-blizzard-700;
    }
  }

  .recommendation {
    @apply absolute -left-px -right-px -top-7 justify-center gap-1 opacity-0 invisible;

    p {
      @apply font-medium text-center text-white text-xs leading-7;

      & + p {
        @apply font-normal underline underline-offset-2 cursor-pointer;
      }
    }
  }

  .outer {
    @apply flex-col p-6 pb-8 bg-white border-2 border-grey-blue-200 rounded-3xl z-0;

    @screen md {
      @apply p-8;
    }

    .h2 {
      @apply font-medium text-2xl leading-8;

      @screen mdminus {
        @apply text-[28px] leading-[34px];
      }

      @screen mdplus {
        @apply text-3xl leading-10;
      }

      & + p {
        @apply mt-1 font-normal text-grey-blue-600 text-sm leading-6;
      }
    }

    .from {
      @apply relative items-center gap-1 h-8 py-[34px] box-content;

      &::after {
        @apply absolute left-[var(--left)] top-8 h-5 w-5 bg-grey-blue-100 rounded-md transform origin-center -translate-x-1/2 -translate-y-3/4 rotate-45 opacity-0 invisible transition-all duration-500 ease-in-out content-[''];
      }

      &::before {
        @apply absolute top-8 px-6 py-[14px] bg-grey-blue-100 text-winter-green-900 text-center text-sm leading-6 rounded shadow-light4 transform -translate-y-full opacity-0 invisible transition-all duration-500 ease-in-out content-[attr(data-information)];
      }

      &.hoverable {
        &::after,
        &::before {
          @apply opacity-100 visible;
        }
      }

      p {
        @apply font-normal text-grey-blue-600 text-sm leading-6;

        :deep(strong) {
          @apply font-bold text-livestorm-blue-600 text-sm leading-6;
        }
      }

      .icon {
        @apply text-grey-blue-600 text-base leading-none;
      }
    }

    .usage,
    .add-ons,
    .services {
      @apply flex-col gap-2 mt-6;

      .h3 {
        @apply text-sm leading-6;
      }

      .list {
        @apply flex-col gap-2;

        .feature {
          @apply relative inline-flex items-start gap-1;

          &::after {
            @apply absolute left-[var(--left)] -top-3 h-5 w-5 bg-grey-blue-100 rounded-md transform origin-center -translate-x-1/2 -translate-y-3/4 rotate-45 opacity-0 invisible transition-all duration-500 ease-in-out content-[''];
          }

          &::before {
            @apply absolute -top-3 px-6 py-[14px] bg-grey-blue-100 text-winter-green-900 text-center text-sm leading-6 rounded shadow-light4 transform -translate-y-full opacity-0 invisible transition-all duration-500 ease-in-out content-[attr(data-information)];
          }

          &.disabled {
            .icon {
              @apply text-grey-blue-300 !important;

              &.icon-info {
                @apply text-grey-blue-400 !important;
              }
            }

            p {
              @apply text-grey-blue-400;
            }
          }

          &.hoverable {
            &::after,
            &::before {
              @apply opacity-100 visible;
            }
          }

          .icon {
            @apply text-grey-blue-600 text-base leading-none transform translate-y-1 !important;

            &.icon-check,
            &.icon-plus {
              @apply font-bold text-livestorm-blue-700 !important;
            }
          }

          p {
            @apply ml-1 font-normal text-grey-blue-600 text-sm leading-6;

            :deep(strong) {
              @apply text-grey-blue-800;
            }
          }
        }
      }
    }

    .integrations {
      @apply flex-col gap-2 mt-6;

      .h3 {
        @apply text-sm leading-6;
      }

      .list {
        @apply flex-wrap gap-2;

        p {
          @apply pt-2 text-grey-blue-800 text-sm leading-6;
        }

        .details {
          @apply gap-1 w-auto p-2 bg-grey-blue-200 rounded;

          .integration {
            @apply relative;

            &::after {
              @apply absolute left-1/2 -top-3 h-5 w-5 bg-grey-blue-100 rounded-md transform origin-center -translate-x-1/2 -translate-y-3/4 rotate-45 opacity-0 invisible transition-all duration-500 ease-in-out content-[''];
            }

            &::before {
              @apply absolute left-1/2 -top-3 px-6 py-[14px] bg-grey-blue-100 text-winter-green-900 text-center text-sm leading-6 rounded shadow-light4 transform -translate-x-1/2 -translate-y-full opacity-0 invisible transition-all duration-500 ease-in-out content-[attr(data-name)];
            }

            &:hover {
              &::after,
              &::before {
                @apply opacity-100 visible;
              }
            }

            .logo {
              @apply rounded-none;
            }
          }
        }
      }
    }

    .separator {
      @apply border-t-2 border-grey-blue-200 mt-6;
    }

    .used-by {
      @apply flex-col gap-2 mt-6;

      .h3 {
        @apply font-normal text-grey-blue-600 text-sm leading-6;
      }

      .list {
        @apply flex-nowrap gap-10;

        .logo {
          @apply rounded-none;

          :deep(img) {
            filter: invert(45%) sepia(13%) saturate(601%) hue-rotate(163deg)
              brightness(87%) contrast(84%);
          }
        }
      }
    }
  }
}
</style>
